import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Chip from '@mui/material/Chip'
import { format, subDays } from 'date-fns'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import KPICard from '../components/KPICard'
import InterventionFilters from '../components/InterventionFilters'
import ChannelBreakdownChart from '../components/ChannelBreakdownChart'
import MerchantBreakdownTable from '../components/MerchantBreakdownTable'
import RuleViewer from '../components/RuleViewer'
import { MOCK_RULES, MOCK_MERCHANT_RULE_CONFIG } from '../mockData'
import { useGetRuleAnalyticsQuery, useGetMerchantRuleConfigQuery } from '../../../redux/interventionApiSlice'

const USE_MOCK = false

export default function RuleDashboard() {
  const [filters, setFilters] = useState({
    type: 'NDR',
    merchantOids: [],
    dateRange: { from: format(subDays(new Date(), 2), 'yyyy-MM-dd'), to: format(new Date(), 'yyyy-MM-dd') },
  })

  const noMerchant = !USE_MOCK && !filters.merchantOids?.length
  const { data: apiData, isFetching } = useGetRuleAnalyticsQuery(filters, { skip: USE_MOCK || noMerchant })
  const { data: apiRuleConfig } = useGetMerchantRuleConfigQuery(
    { merchantOid: filters.merchantOids?.[0], type: 'NDR' },
    { skip: USE_MOCK || !filters.merchantOids?.length })
  const data = USE_MOCK ? MOCK_RULES : apiData
  const ruleConfig = USE_MOCK ? MOCK_MERCHANT_RULE_CONFIG : apiRuleConfig
  const isLoading = USE_MOCK ? false : isFetching

  const actionDonut = data?.actionDistribution ? [
    { label: 'Reattempt', value: data.actionDistribution.reattempt, color: '#2E7D32' },
    { label: 'RTO', value: data.actionDistribution.rto, color: '#D32F2F' },
  ] : []

  const merchantCols = [
    { key: 'merchantName', label: 'Merchant', format: 'text' },
    { key: 'rulesActive', label: 'Active Rules', format: 'number', align: 'right' },
    { key: 'reattemptDeliveryRate', label: 'Reattempt→Delivered %', format: 'percent', align: 'right' },
  ]

  return (
    <Box>
      <Typography variant='h5' sx={{ mb: 2 }}>Rule Engine Analytics</Typography>
      <InterventionFilters filters={filters} onChange={setFilters} />

      {noMerchant ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant='body1' sx={{ color: 'text.disabled' }}>Select a merchant to view data</Typography>
        </Box>
      ) : <>

      <Box className='kpi-row'>
        <KPICard title='Merchants with Rules' value={data?.totalMerchantsWithRules} icon={<AutoFixHighOutlinedIcon />} color='#8B5CF6' loading={isLoading} />
        <KPICard title='Active Rules' value={data?.totalRulesActive} icon={<RuleOutlinedIcon />} color='#0F4C81' loading={isLoading} />
        <KPICard title='Reattempt→Delivered' value={data?.effectiveness?.reattemptToDelivered} icon={<CheckCircleOutlineIcon />} color='#2E7D32' format='percent' loading={isLoading} />
      </Box>

      <Box className='chart-grid-2'>
        <ChannelBreakdownChart data={actionDonut} title='NDR/NPR Action Distribution' loading={isLoading} />
        <Paper sx={{ p: 2.5 }}>
          <Typography variant='subtitle1' sx={{ mb: 1.5 }}>Effectiveness</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600 }}>Auto Reattempts</Typography>
              <Typography variant='h4' sx={{ fontWeight: 800, color: '#2E7D32' }}>
                {data?.actionDistribution?.reattempt?.toLocaleString('en-IN') || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600 }}>Auto RTO Initiated</Typography>
              <Typography variant='h4' sx={{ fontWeight: 800, color: '#D32F2F' }}>
                {data?.actionDistribution?.rto?.toLocaleString('en-IN') || '—'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Sample Rule Config */}
      {ruleConfig && (
        <Paper sx={{ p: 2.5, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant='subtitle1'>Rule Configuration Sample</Typography>
            <Chip label={ruleConfig.merchantName} size='small' variant='outlined' />
            <Chip label={ruleConfig.ndrRuleDefinition?.status} size='small' sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 700 }} />
          </Box>
          {ruleConfig.ndrRuleDefinition?.rules?.map((rule, idx) => (
            <RuleViewer key={idx} rule={rule} index={idx} />
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip label={`WhatsApp: ${ruleConfig.whatsappAutomatedNdr ? 'ON' : 'OFF'}`} size='small' sx={{ bgcolor: ruleConfig.whatsappAutomatedNdr ? '#E8F9EE' : '#F4F6F8', color: ruleConfig.whatsappAutomatedNdr ? '#25D366' : '#919EAB', fontWeight: 700 }} />
            <Chip label={`Futwork: ${ruleConfig.futworksNdrConfig?.pushLead ? 'ON' : 'OFF'}`} size='small' sx={{ bgcolor: ruleConfig.futworksNdrConfig?.pushLead ? '#FFF4E5' : '#F4F6F8', color: ruleConfig.futworksNdrConfig?.pushLead ? '#FF6B35' : '#919EAB', fontWeight: 700 }} />
            <Chip label={`FutworkAI: ${ruleConfig.futworkAINdrConfig?.pushLead ? 'ON' : 'Not Activated'}`} size='small' sx={{ bgcolor: '#F4F6F8', color: '#919EAB', fontWeight: 700 }} />
            <Chip label={`Managed by: ${ruleConfig.ndrManagedByProzo ? 'Prozo' : 'Courier'}`} size='small' variant='outlined' />
          </Box>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <MerchantBreakdownTable
          data={data?.merchantBreakdown || []}
          columns={merchantCols}
          title='Rules by Merchant'
          loading={isLoading}
          defaultSort={{ field: 'rulesActive', direction: 'desc' }}
        />
      </Box>
      </>}
    </Box>
  )
}
